import type { ClientToolCall, ExecuteClientToolDeps } from './client-tool-types'
import { executeRequestUserConfirmation } from './confirmClientTool'
import { executeEditForm } from './execute-edit-form'
import { executeEditNotebook } from './execute-edit-notebook'
import {
  executeAddLayers,
  executeListBasemaps,
  executeRemoveLayer,
  executeSaveMap,
  executeSetBasemap,
  executeSetMapSettings,
  executeStyleLayer,
  executeToggleLayer,
  executeUpdateMapLayers,
  executeZoomToCoordinates,
  executeZoomToLayer,
} from './execute-map-tools'
import { executeNavigateTo, LEAST_PRIVILEGED_CALLER, resolveFeNavigationTarget } from './execute-navigate-to'
import { executeOpenAddUser } from './execute-open-add-user'
import { executeOpenImportDialog } from './execute-open-import-dialog'
import { executeReadOpenNotebook } from './execute-read-open-notebook'
import { executeRunCells } from './execute-run-cells'
import { executeShowForm } from './execute-show-form'
import { executeShowGeocoding } from './execute-show-geocoding'
import { executeShowLibrary } from './execute-show-library'
import { executeShowMonitoring } from './execute-show-monitoring'
import { executeShowNotebook } from './execute-show-notebook'
import { executeShowSecurity } from './execute-show-security'
import { executeShowSettings } from './execute-show-settings'

// The client tools, one module per family: navigation (`execute-navigate-to`), the map
// (`execute-map-tools`), the Library (`execute-show-library`, `execute-open-import-dialog`),
// geocoding (`execute-show-geocoding`), Forms (`execute-show-form`, `execute-edit-form`), Scripts (`execute-show-notebook`; the live notebook: `execute-read-open-notebook`, `execute-edit-notebook`, `execute-run-cells`) and the Yes/No prompt (`confirmClientTool`). This module only dispatches.
export type { ClientToolCall, ExecuteClientToolDeps } from './client-tool-types'
export { resolveFeNavigationTarget } from './execute-navigate-to'

/**
 * Execute ONE client tool call and resolve to its result.
 *
 * NEVER throws: an unknown tool name, malformed input, or a throwing executor
 * all resolve to an `{ error }` result. A throw here would leave the server's
 * run suspended forever waiting for a result that can never arrive, so the
 * model must always get something back — even if that something is a failure
 * it can adapt to.
 */
export async function executeClientTool(call: ClientToolCall, deps: ExecuteClientToolDeps): Promise<unknown> {
  try {
    switch (call.name) {
      case 'navigate_to':
        return await executeNavigateTo(call.input, deps)
      case 'add_layer_to_map':
        return await executeAddLayers(call.input)
      case 'toggle_layer_visibility':
        return await executeToggleLayer(call.input)
      case 'remove_layer_from_map':
        return await executeRemoveLayer(call.input)
      case 'zoom_to_layer':
        return await executeZoomToLayer(call.input)
      case 'zoom_to_coordinates':
        return await executeZoomToCoordinates(call.input)
      case 'save_map':
        return await executeSaveMap(call.input, deps)
      case 'update_map_layers':
        return await executeUpdateMapLayers(call.input)
      case 'list_basemaps':
        return await executeListBasemaps()
      case 'set_basemap':
        return await executeSetBasemap(call.input)
      case 'set_map_settings':
        return await executeSetMapSettings(call.input)
      case 'style_layer':
        return await executeStyleLayer(call.input)
      case 'show_library': {
        const allowed = resolveFeNavigationTarget('/library', deps.caller ?? LEAST_PRIVILEGED_CALLER)

        return await executeShowLibrary(call.input, deps, allowed.ok ? null : allowed.reason)
      }

      case 'open_import_dialog': {
        const allowed = resolveFeNavigationTarget('/library', deps.caller ?? LEAST_PRIVILEGED_CALLER)

        return await executeOpenImportDialog(call.input, deps, allowed.ok ? null : allowed.reason)
      }

      case 'show_geocoding': {
        const allowed = resolveFeNavigationTarget('/geocoding', deps.caller ?? LEAST_PRIVILEGED_CALLER)

        return await executeShowGeocoding(call.input, deps, allowed.ok ? null : allowed.reason)
      }

      case 'show_form': {
        const allowed = resolveFeNavigationTarget('/forms', deps.caller ?? LEAST_PRIVILEGED_CALLER)

        return await executeShowForm(call.input, deps, allowed.ok ? null : allowed.reason)
      }

      case 'show_notebook': {
        const allowed = resolveFeNavigationTarget('/scripts', deps.caller ?? LEAST_PRIVILEGED_CALLER)

        return await executeShowNotebook(call.input, deps, allowed.ok ? null : allowed.reason)
      }

      case 'show_security': {
        const allowed = resolveFeNavigationTarget('/security', deps.caller ?? LEAST_PRIVILEGED_CALLER)

        return await executeShowSecurity(call.input, deps, allowed.ok ? null : allowed.reason)
      }

      // The server gate picks /capacity or /monitoring by view; the target is resolved the same way per call.
      case 'show_monitoring': {
        const caller = deps.caller ?? LEAST_PRIVILEGED_CALLER

        return await executeShowMonitoring(call.input, deps, route => {
          const allowed = resolveFeNavigationTarget(route, caller)

          return allowed.ok ? null : allowed.reason
        })
      }

      case 'open_add_user': {
        const allowed = resolveFeNavigationTarget('/settings/users', deps.caller ?? LEAST_PRIVILEGED_CALLER)

        return await executeOpenAddUser(call.input, deps, allowed.ok ? null : allowed.reason)
      }

      // The server gate picks /settings/users or /settings/groups by view; the target is resolved the same way per call.
      case 'show_settings': {
        const caller = deps.caller ?? LEAST_PRIVILEGED_CALLER

        return await executeShowSettings(call.input, deps, route => {
          const allowed = resolveFeNavigationTarget(route, caller)

          return allowed.ok ? null : allowed.reason
        })
      }

      case 'edit_form':
        return await executeEditForm(call.input, deps)

      // The notebook open in a live session, through the JupyterLab bridge (no route gate: they act only on a page already open).
      case 'read_open_notebook':
        return await executeReadOpenNotebook(call.input)
      case 'edit_notebook':
        return await executeEditNotebook(call.input)
      case 'run_cells':
        return await executeRunCells(call.input)

      case 'request_user_confirmation':
        return await executeRequestUserConfirmation(call.input, deps.confirm)
      default:
        return { error: `Unknown client tool "${call.name}".` }
    }
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) }
  }
}

/**
 * Execute EVERY call in one `client_tool_request` batch, in order.
 *
 * The server requires every pending call answered exactly once and rejects
 * duplicate `toolCallId`s, so the caller sends ONE `SEND_TOOL_RESULT` carrying
 * this whole array — a partial batch dead-ends the turn.
 */
export async function executeClientToolBatch(
  calls: readonly ClientToolCall[],
  deps: ExecuteClientToolDeps,
): Promise<Array<{ toolCallId: string; result: unknown }>> {
  const results: Array<{ toolCallId: string; result: unknown }> = []

  for (const call of calls) {
    results.push({ toolCallId: call.id, result: await executeClientTool(call, deps) })
  }

  return results
}
